<template>
    <div class="email-form">
        <form-wrapper @submit="handleFormSubmission(data)">
            <common-content-spacer height="tiny">
                <form-row>
                    <form-cell>
                        <common-page-header :heading="headerText" size="large"></common-page-header>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.to')"
                            :placeholder="toPlaceholder"
                            :isRequired="false"
                            :formData="data"
                            valuePath="to"
                            @focus="openSelectMemberModal"
                        ></text-field>
                        <common-labels
                            v-if="selectedUsers.length"
                            :labels="selectedUsers"
                            @click="unselectUser($event)"
                        ></common-labels>
                    </form-cell>
                    <form-cell class="email-form__open-search">
                        <common-button
                            class="open-search-network-button"
                            :isDisabled="false"
                            :isSubmittable="false"
                            :icon="showSelectToData ? 'close' : 'plus-circle'"
                            size="micro"
                            type="tertiary"
                            @click="toggleSelectMemberModal"
                        ></common-button>

                        <common-dropdown
                            :recalculatePosition="true"
                            :isOpen="showSelectToData"
                            :hasArrow="true"
                            bgColor="white"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <search-network-members
                                :selectedUsers="selectedUsers"
                                class="search-members-modal"
                                :placeholder="$t('forms.email_form.placeholder.search')"
                                icon="search"
                                iconPosition="right"
                                iconBackground="primary"
                                @user-selected="selectUser"
                            ></search-network-members>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row v-if="showCcField">
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.cc')"
                            :placeholder="ccPlaceholder"
                            :formData="data"
                            valuePath="cc"
                            @change="change($event)"
                        ></text-field>
                        <common-labels
                            v-if="selectedEmailsForCcField.length"
                            :labels="selectedEmailsForCcField"
                            @click="removeCcEmail($event)"
                        ></common-labels>
                    </form-cell>
                    <form-cell class="email-form__open-search">
                        <common-button
                            class="open-search-network-button"
                            :isDisabled="false"
                            :isSubmittable="false"
                            :icon="showSelectCcData ? 'close' : 'plus-circle'"
                            size="micro"
                            type="tertiary"
                            @click="toggleSelectCcModal"
                        ></common-button>

                        <common-dropdown
                            v-clickaway="toggleSelectCcModal"
                            :recalculatePosition="true"
                            :isOpen="showSelectCcData"
                            :hasArrow="true"
                            bgColor="white"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <search-network-members
                                :selectedUsers="selectedUsers"
                                class="search-members-modal"
                                :placeholder="$t('forms.email_form.placeholder.search')"
                                icon="search"
                                iconPosition="right"
                                iconBackground="primary"
                                @user-selected="addCcEmail"
                            ></search-network-members>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row v-if="showBccField">
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.cc')"
                            :placeholder="ccPlaceholder"
                            :formData="data"
                            valuePath="bcc"
                            @change="change($event)"
                        ></text-field>
                        <common-labels
                            v-if="selectedEmailsForBccField.length"
                            :labels="selectedEmailsForBccField"
                            @click="removeBccEmail($event)"
                        ></common-labels>
                    </form-cell>
                    <form-cell class="email-form__open-search">
                        <common-button
                            class="open-search-network-button"
                            :isDisabled="false"
                            :isSubmittable="false"
                            :icon="showSelectCcData ? 'close' : 'plus-circle'"
                            size="micro"
                            type="tertiary"
                            @click="toggleSelectBccModal"
                        ></common-button>

                        <common-dropdown
                            v-clickaway="toggleSelectBccModal"
                            :recalculatePosition="true"
                            :isOpen="showSelectBccData"
                            :hasArrow="true"
                            bgColor="white"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <search-network-members
                                :selectedUsers="selectedUsers"
                                class="search-members-modal"
                                :placeholder="$t('forms.email_form.placeholder.search')"
                                icon="search"
                                iconPosition="right"
                                iconBackground="primary"
                                @user-selected="addBccEmail"
                            ></search-network-members>
                        </common-dropdown>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.subject')"
                            :placeholder="$t('forms.email_form.form_labels.subject')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="subject"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.email_form.form_labels.from')"
                            :placeholder="$t('forms.email_form.form_labels.from')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="from"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-editor-field
                            :label="$t('forms.email_form.form_labels.message')"
                            :placeholder="$t('forms.email_form.placeholder.message')"
                            :isRequired="true"
                            :uploadedFiles="[]"
                            :formData="data"
                            :attachmentHintText="$t('forms.email_form.cursor_message.attach_button')"
                            valuePath="message"
                            @change="change($event)"
                        ></text-editor-field>
                    </form-cell>
                </form-row>
                <common-buttons>
                    <common-button
                        :isProcessing="isSending"
                        :isDisabled="isSendDisabled"
                        :label="$t('forms.terms_of_use.support.buttons.send')"
                        type="primary"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </form-wrapper>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import SearchNetworkMembers from 'components/page-elements/connections/SearchNetworkMembers'
    import TextEditorField from '../../form-fields/TextEditorField.vue'

    export default {
        mixins: [FormMixin],
        components: { SearchNetworkMembers, TextEditorField },
        props: {
            headerText: { type: String, default: 'Email Blast' },
            showOpenSelectMemberModalButton: { type: Boolean, default: false },
            showCcField: { type: Boolean, default: false },
            showBccField: { type: Boolean, default: false },
            showBccWarning: { type: Boolean, default: false },
        },
        data() {
            return {
                isSending: false,
                showSelectToData: false,
                showSelectCcData: false,
                showSelectBccData: false,
                selectedUsers: [],
                selectedEmailsForCcField: [],
                selectedEmailsForBccField: [],
            }
        },
        computed: {
            toPlaceholder() {
                return !this.selectedUsers.length ? this.$t('forms.email_form.placeholder.to') : ''
            },
            ccPlaceholder() {
                return !this.selectedEmailsForCcField.length ? this.$t('forms.email_form.form_labels.cc') : ''
            },
            isSendDisabled() {
                return !(this.data.subject && this.data.message && this.data.from && this.selectedUsers.length)
            },
        },
        methods: {
            closeSelectMemberModal() {
                this.showSelectToData = false
            },
            openSelectMemberModal() {
                if (this.showOpenSelectMemberModalButton) {
                    this.showSelectToData = true
                }
            },
            toggleSelectMemberModal() {
                this.showSelectToData = !this.showSelectToData
            },
            toggleSelectCcModal() {
                this.showSelectCcData = !this.showSelectCcData
            },
            toggleSelectBccModal() {
                this.showSelectBccData = !this.showSelectBccData
            },
            handleFormSubmission({ subject, message, from }) {
                let to = []
                let cc = []
                let bcc = []
                this.selectedUsers.forEach((u) => {
                    to.push(u.label)
                })
                this.selectedEmailsForCcField.forEach((user) => {
                    cc.push(user.label)
                })
                this.selectedEmailsForBccField.forEach((user) => {
                    bcc.push(user.label)
                })
                this.emitEvent({
                    name: 'email-data',
                    payload: { to: to, cc: cc, bcc: bcc, subject: subject, message: message, from: from },
                })
                this.clearFormData()
            },

            clearFormData() {
                this.data.message = null
                this.data.subject = null
                this.data.cc = null
                this.selectedUsers = []
                this.selectedEmailsForCcField = []
                this.selectedEmailsForBccField = []
            },
            unselectUser(ev) {
                this.selectedUsers.forEach((user, index) => {
                    if (user.id === ev.id) {
                        this.selectedUsers.splice(index, 1)
                    }
                })
            },
            selectUser(user) {
                const index = this.selectedUsers.findIndex((item) => {
                    return item.id === user.id
                })
                if (index === -1) {
                    this.$set(this.selectedUsers, this.selectedUsers.length, {
                        id: user.id,
                        label: user.email,
                    })
                } else {
                    this.selectedUsers.splice(index, 1)
                }
            },
            removeCcEmail(ev) {
                this.selectedEmailsForCcField.forEach((user, index) => {
                    if (user.label === ev.label) {
                        this.selectedEmailsForCcField.splice(index, 1)
                    }
                })
            },
            removeBccEmail(ev) {
                this.selectedEmailsForBccField.forEach((user, index) => {
                    if (user.label === ev.label) {
                        this.selectedEmailsForBccField.splice(index, 1)
                    }
                })
            },
            addCcEmail(user) {
                let exist = false
                this.selectedEmailsForCcField.forEach((u, index) => {
                    if (u.id === user.id) {
                        this.selectedEmailsForCcField.splice(index, 1)
                        exist = true
                    }
                })
                if (!exist) {
                    this.$set(this.selectedEmailsForCcField, this.selectedEmailsForCcField.length, {
                        id: user.id,
                        label: user.email,
                    })
                }
            },
            addBccEmail(user) {
                let exist = false
                this.selectedEmailsForBccField.forEach((u, index) => {
                    if (u.id === user.id) {
                        this.selectedEmailsForBccField.splice(index, 1)
                        exist = true
                    }
                })
                if (!exist) {
                    this.$set(this.selectedEmailsForBccField, this.selectedEmailsForBccField.length, {
                        id: user.id,
                        label: user.email,
                    })
                }
            },
        },
        mounted() {
            const { firstName, lastName, email } = this.$auth.getActiveMember()
            this.$set(this.data, 'from', firstName + ' ' + lastName + ' - ' + email)
        },
    }
</script>
